import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RotateCcw, Check, X, Package, DollarSign, Tag } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface ReturnRequest {
  id: string;
  order_id: string;
  order_number: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  reason: string;
  customer_notes: string | null;
  admin_notes: string | null;
  refund_amount: number | null;
  return_tracking_number: string | null;
  return_tracking_carrier: string | null;
  return_label_url: string | null;
  item_count: number;
  order_total: number;
  created_at: string;
  items?: Array<{
    product_name: string;
    variant_name: string | null;
    quantity: number;
    unit_price: number;
  }>;
}

const STATUS_BADGES: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'gray'> = {
  requested: 'yellow',
  approved: 'blue',
  rejected: 'red',
  label_sent: 'blue',
  received: 'blue',
  refunded: 'green',
  closed: 'gray',
};

interface ReturnRate {
  carrier: string;
  service_name: string;
  service_code: string;
  price: number;
  estimated_days_min?: number | null;
}

type ActionType = 'approve' | 'reject' | 'receive' | 'refund' | 'label';

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Requested', value: 'requested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Label sent', value: 'label_sent' },
  { label: 'Received', value: 'received' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Rejected', value: 'rejected' },
];

export default function AdminReturns() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';

  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Action modals
  const [selectedReturn, setSelectedReturn] = useState<ReturnRequest | null>(null);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [restoreInventory, setRestoreInventory] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  // Return label modal
  const [labelRates, setLabelRates] = useState<ReturnRate[] | null>(null);
  const [labelRatesReason, setLabelRatesReason] = useState('');
  const [selectedRate, setSelectedRate] = useState('');
  const [manualTracking, setManualTracking] = useState('');
  const [manualCarrier, setManualCarrier] = useState('');
  const [manualLabelUrl, setManualLabelUrl] = useState('');

  useEffect(() => { fetchReturns(); }, [statusFilter]);

  async function fetchReturns() {
    setIsLoading(true);
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      const res = await api.get<{ returns: ReturnRequest[] }>(`/admin/returns${query}`);
      setReturns(res.returns);
    } catch { /* ignore */ } finally { setIsLoading(false); }
  }

  function openAction(ret: ReturnRequest, type: ActionType) {
    setSelectedReturn(ret);
    setActionType(type);
    setAdminNotes('');
    setRefundAmount(ret.order_total?.toString() || '');
    setRestoreInventory(true);
    if (type === 'label') {
      setLabelRates(null);
      setLabelRatesReason('');
      setSelectedRate('');
      setManualTracking(ret.return_tracking_number || '');
      setManualCarrier(ret.return_tracking_carrier || '');
      setManualLabelUrl(ret.return_label_url || '');
      api.get<{ rates: ReturnRate[]; reason?: string }>(`/admin/returns/${ret.id}/label-rates`)
        .then((r) => { setLabelRates(r.rates); setLabelRatesReason(r.reason || ''); if (r.rates[0]) setSelectedRate(r.rates[0].service_code); })
        .catch((err: any) => { setLabelRates([]); setLabelRatesReason(err?.message || 'Could not load return rates'); });
    }
  }

  async function handleAction() {
    if (!selectedReturn || !actionType) return;
    setIsProcessing(true);
    try {
      if (actionType === 'refund') {
        await api.put(`/admin/returns/${selectedReturn.id}/refund`, {
          refund_amount: parseFloat(refundAmount),
          restore_inventory: restoreInventory,
        });
      } else if (actionType === 'label') {
        if (selectedRate) {
          await api.post(`/admin/returns/${selectedReturn.id}/label`, { service_code: selectedRate });
        } else {
          await api.put(`/admin/returns/${selectedReturn.id}/label`, {
            tracking_number: manualTracking || undefined,
            carrier: manualCarrier || undefined,
            label_url: manualLabelUrl || undefined,
          });
        }
      } else {
        await api.put(`/admin/returns/${selectedReturn.id}/${actionType}`, {
          admin_notes: adminNotes || undefined,
        });
      }
      setActionType(null);
      setSelectedReturn(null);
      fetchReturns();
    } catch (err: any) {
      alert(err.message || 'Action failed');
    } finally { setIsProcessing(false); }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Returns</h1>

      {/* Status Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {STATUS_TABS.map(tab => (
          <button key={tab.value}
            onClick={() => setSearchParams(tab.value ? { status: tab.value } : {})}
            className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
              statusFilter === tab.value ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : returns.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-8">
            <RotateCcw className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">No return requests found.</p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {returns.map(ret => (
                  <tr key={ret.id}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/orders/${ret.order_id}`} className="text-sm text-blue-600 hover:underline">
                        {ret.order_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {ret.first_name} {ret.last_name}
                      <div className="text-xs text-gray-500">{ret.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{ret.reason}</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_BADGES[ret.status] || 'gray'}>{ret.status}</Badge>
                      {ret.return_tracking_number && (
                        <div className="text-xs text-gray-500 mt-1">
                          {ret.return_tracking_carrier ? `${ret.return_tracking_carrier.toUpperCase()} ` : ''}{ret.return_tracking_number}
                          {ret.return_label_url && <> · <a href={ret.return_label_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">label</a></>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(ret.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        {ret.status === 'requested' && (
                          <>
                            <Button size="sm" variant="primary" onClick={() => openAction(ret, 'approve')}>
                              <Check className="h-3 w-3 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => openAction(ret, 'reject')}>
                              <X className="h-3 w-3 mr-1" />Reject
                            </Button>
                          </>
                        )}
                        {ret.status === 'approved' && (
                          <Button size="sm" variant="outline" onClick={() => openAction(ret, 'label')}>
                            <Tag className="h-3 w-3 mr-1" />Send label
                          </Button>
                        )}
                        {['approved', 'label_sent'].includes(ret.status) && (
                          <Button size="sm" variant="outline" onClick={() => openAction(ret, 'receive')}>
                            <Package className="h-3 w-3 mr-1" />Received
                          </Button>
                        )}
                        {ret.status === 'received' && (
                          <Button size="sm" variant="primary" onClick={() => openAction(ret, 'refund')}>
                            <DollarSign className="h-3 w-3 mr-1" />Refund
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Action Modal */}
      <Modal
        isOpen={!!actionType && !!selectedReturn}
        onClose={() => { setActionType(null); setSelectedReturn(null); }}
        title={actionType === 'label' ? 'Send Return Label' : actionType ? `${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Return` : ''}
      >
        <div className="space-y-4">
          {selectedReturn && (
            <p className="text-sm text-gray-600">
              Order <strong>{selectedReturn.order_number}</strong> - {selectedReturn.reason}
            </p>
          )}

          {actionType === 'label' ? (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Buy a prepaid label (charged to your Shippo account)</p>
                {labelRates === null ? (
                  <p className="text-sm text-gray-500">Loading rates…</p>
                ) : labelRates.length === 0 ? (
                  <p className="text-sm text-gray-500">{labelRatesReason || 'No return rates available for this address.'}</p>
                ) : (
                  <div className="space-y-1">
                    {labelRates.map((r) => (
                      <label key={r.service_code} className="flex items-center justify-between gap-3 rounded border border-gray-200 px-3 py-2 text-sm">
                        <span className="flex items-center gap-2">
                          <input type="radio" name="return-rate" checked={selectedRate === r.service_code} onChange={() => setSelectedRate(r.service_code)} />
                          {r.service_name}{r.estimated_days_min ? <span className="text-gray-400"> · {r.estimated_days_min}d</span> : null}
                        </span>
                        <span className="font-medium">${r.price.toFixed(2)}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm text-gray-600 pt-1">
                      <input type="radio" name="return-rate" checked={selectedRate === ''} onChange={() => setSelectedRate('')} />
                      Record a label I bought elsewhere instead
                    </label>
                  </div>
                )}
              </div>
              {selectedRate === '' && (
                <div className="space-y-3">
                  <Input label="Tracking number" value={manualTracking} onChange={e => setManualTracking(e.target.value)} />
                  <Input label="Carrier (usps, ups, fedex, dhl…)" value={manualCarrier} onChange={e => setManualCarrier(e.target.value)} />
                  <Input label="Label URL (optional)" value={manualLabelUrl} onChange={e => setManualLabelUrl(e.target.value)} placeholder="https://…" />
                </div>
              )}
              <p className="text-xs text-gray-500">The customer gets an email with the label and tracking; the return moves to "label sent".</p>
            </div>
          ) : actionType === 'refund' ? (
            <>
              <Input label="Refund Amount" type="number" step="0.01" value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)} />
              <div className="flex items-center gap-2">
                <input type="checkbox" id="restore-inv" checked={restoreInventory}
                  onChange={e => setRestoreInventory(e.target.checked)} className="rounded border-gray-300" />
                <label htmlFor="restore-inv" className="text-sm text-gray-700">Restore inventory</label>
              </div>
            </>
          ) : (
            <Textarea label="Admin Notes (optional)" value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)} rows={3} />
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => { setActionType(null); setSelectedReturn(null); }}>Cancel</Button>
            <Button
              variant={actionType === 'reject' ? 'danger' : 'primary'}
              onClick={handleAction}
              isLoading={isProcessing}
            >
              {actionType === 'approve' && 'Approve Return'}
              {actionType === 'reject' && 'Reject Return'}
              {actionType === 'receive' && 'Mark Received'}
              {actionType === 'refund' && 'Process Refund'}
              {actionType === 'label' && (selectedRate ? 'Buy & Send Label' : 'Save & Notify')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
